import type { Dayjs } from "dayjs"

import type {
  ReportExportFormat,
  ReportSchedule,
  ReportScheduleFrequency,
  UpdateReportScheduleBody
} from "../types/collab.types.ts"
import type { ReportOwner } from "../types/types.ts"

export const SCHEDULE_FREQUENCIES: readonly ReportScheduleFrequency[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly"
] as const

export const SCHEDULE_FORMATS: readonly ReportExportFormat[] = [
  "pdf",
  "xlsx",
  "csv"
] as const

// RFC 5322-lite: good enough to reject obvious typos without rejecting valid,
// unusual addresses.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isValidEmail = (value: string): boolean =>
  EMAIL_RE.test(value.trim())

export const usesWeekday = (frequency: ReportScheduleFrequency): boolean =>
  frequency === "weekly"

export const usesDayOfMonth = (frequency: ReportScheduleFrequency): boolean =>
  frequency === "monthly" || frequency === "quarterly"

export interface ScheduleFormValues {
  enabled: boolean
  frequency: ReportScheduleFrequency
  weekday?: number | null
  day_of_month?: number | null
  send_time: string
  recipients_users: (ReportOwner | number | string)[]
  recipients_emails: string[]
  formats: ReportExportFormat[]
}

// The backend reads recipients as nested {id, ...} objects but the PUT accepts a
// bare id array; tolerate either so a shape change on the read side never leaks
// objects into the payload.
export const recipientUserId = (
  user: ReportOwner | number | string
): number | string =>
  typeof user === "object" && user !== null ? user.id : user

export interface ScheduleRecipientOption {
  label: string
  value: number | string
}

export const recipientDisplayName = (user: ReportOwner): string =>
  [user.first_name, user.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ") || String(user.id)

// Seed the picker with the stored recipients (keyed by their own ids) so members
// who are no longer in the team option set still render with their name, then
// append the picker options that aren't already represented.
export const mergeRecipientOptions = (
  storedUsers: ReportOwner[],
  pickerOptions: ScheduleRecipientOption[]
): ScheduleRecipientOption[] => {
  const seen = new Set<string>()
  const merged: ScheduleRecipientOption[] = []
  const push = (option: ScheduleRecipientOption) => {
    const key = String(option.value)
    if (seen.has(key)) return
    seen.add(key)
    merged.push(option)
  }
  for (const user of storedUsers)
    push({ label: recipientDisplayName(user), value: user.id })
  for (const option of pickerOptions) push(option)
  return merged
}

export const DEFAULT_SCHEDULE_FORM: ScheduleFormValues = {
  enabled: true,
  frequency: "weekly",
  weekday: 1,
  day_of_month: 1,
  send_time: "09:00",
  recipients_users: [],
  recipients_emails: [],
  formats: ["pdf"]
}

export const scheduleToFormValues = (
  schedule: ReportSchedule | null
): ScheduleFormValues => {
  if (!schedule) return DEFAULT_SCHEDULE_FORM
  return {
    enabled: schedule.enabled,
    frequency: schedule.frequency,
    weekday: schedule.weekday ?? DEFAULT_SCHEDULE_FORM.weekday,
    day_of_month: schedule.day_of_month ?? DEFAULT_SCHEDULE_FORM.day_of_month,
    send_time: schedule.send_time || DEFAULT_SCHEDULE_FORM.send_time,
    recipients_users: schedule.recipients_users.map(recipientUserId),
    recipients_emails: schedule.recipients_emails,
    formats: schedule.formats.length ? schedule.formats : ["pdf"]
  }
}

export interface ScheduleValidationResult {
  valid: boolean
  errors: {
    formats?: string
    emails?: string
    weekday?: string
    dayOfMonth?: string
  }
}

export const validateScheduleForm = (
  values: ScheduleFormValues
): ScheduleValidationResult => {
  const errors: ScheduleValidationResult["errors"] = {}

  if (!values.enabled) return { valid: true, errors }

  if (values.formats.length === 0) errors.formats = "formatsRequired"

  const invalidEmail = values.recipients_emails.find(
    (email) => !isValidEmail(email)
  )
  if (invalidEmail) errors.emails = "invalidEmail"

  if (usesWeekday(values.frequency) && values.weekday == null)
    errors.weekday = "weekdayRequired"

  if (usesDayOfMonth(values.frequency)) {
    const day = values.day_of_month
    if (day == null || day < 1 || day > 28)
      errors.dayOfMonth = "dayOfMonthRange"
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

const QUARTER_MONTH_INDEXES = [0, 3, 6, 9]

// Client-side mirror of the backend's compute_next_run (apps/reports/schedules.py)
// so the drawer can preview the next fire time while the form is being edited.
export const computeNextRunPreview = (
  values: Pick<
    ScheduleFormValues,
    "frequency" | "weekday" | "day_of_month" | "send_time"
  >,
  now: Dayjs
): Dayjs | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(values.send_time ?? "")
  if (!match) return null
  const at = (day: Dayjs) =>
    day.hour(Number(match[1])).minute(Number(match[2])).second(0).millisecond(0)
  const dayOfMonth = values.day_of_month ?? 1

  switch (values.frequency) {
    case "daily": {
      const candidate = at(now)
      return candidate.isAfter(now) ? candidate : candidate.add(1, "day")
    }
    case "weekly": {
      const mondayIndexedToday = (now.day() + 6) % 7
      const target = values.weekday ?? 0
      const candidate = at(now).add(
        (target - mondayIndexedToday + 7) % 7,
        "day"
      )
      return candidate.isAfter(now) ? candidate : candidate.add(7, "day")
    }
    case "monthly": {
      const candidate = at(now.date(dayOfMonth))
      return candidate.isAfter(now) ? candidate : candidate.add(1, "month")
    }
    case "quarterly": {
      for (const yearOffset of [0, 1]) {
        for (const month of QUARTER_MONTH_INDEXES) {
          const candidate = at(
            now.date(1).add(yearOffset, "year").month(month).date(dayOfMonth)
          )
          if (candidate.isAfter(now)) return candidate
        }
      }
      return null
    }
    default:
      return null
  }
}

export const buildSchedulePayload = (
  values: ScheduleFormValues
): UpdateReportScheduleBody => ({
  enabled: values.enabled,
  frequency: values.frequency,
  weekday: usesWeekday(values.frequency) ? (values.weekday ?? null) : null,
  day_of_month: usesDayOfMonth(values.frequency)
    ? (values.day_of_month ?? null)
    : null,
  send_time: values.send_time,
  recipients_users: values.recipients_users.map(recipientUserId),
  recipients_emails: values.recipients_emails.map((email) => email.trim()),
  formats: values.formats
})

const SCHEDULE_ERROR_FIELDS = [
  "recipients_users",
  "recipients_emails",
  "formats",
  "send_time",
  "frequency",
  "weekday",
  "day_of_month"
] as const

// The envelope nests DRF field errors beside a generic details.message, so the
// first field-level string is the informative one.
export const scheduleSaveErrorMessage = (response: {
  data?: { details?: Record<string, unknown> | null }
}): string | undefined => {
  const details = response.data?.details
  if (!details) return undefined
  for (const field of SCHEDULE_ERROR_FIELDS) {
    const value = details[field]
    if (typeof value === "string" && value.trim()) return value
    if (Array.isArray(value) && typeof value[0] === "string")
      return value.join(" ")
  }
  const message = details.message
  return typeof message === "string" &&
    message.trim() &&
    message !== "Validation error"
    ? message
    : undefined
}
